import { designate } from "../../components/attention";
import { t } from "../../i18n/i18n";
import { centerOutcome, mapDropTarget } from "../../map/units/listDrag";
import { useCompositionWrites } from "../../map/units/useCompositionWrites";
import { usePendingZone } from "../../map/zones/ZoneWaitCallout";
import { designateZoneWait } from "../../map/zones/zoneAttention";
import { refuseIfDossierLocked } from "../../sharing/access";
import { useUi } from "../../state/ui";

/** Actions bloquées et gestes communs aux listes d'unités du dossier (docs/18 §6.5) : dossier non enregistré
 *  (l'enregistrement est désigné), lecture seule (le bandeau), terrain en préparation (l'attente de la zone) ; pose
 *  au centre de la carte avec le même contrôle de l'emprise qu'un dépôt. */
export function useUnitActions() {
  const writes = useCompositionWrites();
  const phase = useUi((s) => s.phase);
  const notify = useUi((s) => s.notify);
  const requestSave = useUi((s) => s.requestSave);
  const exerciseId = useUi((s) => s.exerciseId);
  const pendingZone = usePendingZone();

  /** Écriture refusée avant l'envoi : lecture seule, terrain en préparation ou dossier à enregistrer. */
  const refuse = () => {
    if (refuseIfDossierLocked()) return; // lecture seule (docs/17 §3.2) : le bandeau, pas l'enregistrement
    if (pendingZone) return designateZoneWait(pendingZone, "terrain");
    requestSave();
    notify({
      text: exerciseId
        ? t("Enregistrez les modifications du dossier pour composer ses forces")
        : t("Créez le dossier pour composer ses forces"),
      detail: t("La composition, les rôles et les unités posées sont mémorisés dans le dossier d'exercice."),
      tone: "info",
    });
  };

  /** Lance `run` si l'écriture est possible, sinon désigne l'action attendue. */
  const guard = (run: () => void) => {
    if (!writes.canEdit || pendingZone) return refuse();
    run();
  };

  const place = (key: string, at: [number, number]) => writes.place.mutate({ unit: key, phase, lon: at[0], lat: at[1] });

  /** Pose au centre de la carte (menu, au clavier) : même contrôle de l'emprise qu'un dépôt. */
  const placeAtCenter = (key: string) => {
    if (!writes.canEdit || pendingZone) return refuse();
    const map = mapDropTarget();
    const outcome = centerOutcome(map);
    if (outcome.kind === "place") return place(key, outcome.at);
    if (!map || outcome.kind === "outside-map") {
      notify({ text: t("Unité non posée"), detail: t("La carte n'est pas encore chargée."), tone: "info" });
      return;
    }
    // Centre de la carte hors de l'emprise : « Cadrer sur le camp » (ou la zone), désigné s'il est actif.
    const free = map.freeZone();
    const fit = document.querySelector<HTMLButtonElement>('[data-attention="map-fit"]');
    const canFit = Boolean(fit && !fit.disabled);
    if (canFit) designate(fit);
    notify({
      text: free ? t("Hors du terrain") : t("Hors du camp"),
      detail: free
        ? t("Déposez l'unité sur la carte, dans l'emprise du terrain.")
        : t("Déposez l'unité sur la carte, dans l'emprise du camp."),
      tone: "warn",
      action: canFit ? { label: free ? t("Cadrer sur le terrain") : t("Cadrer sur le camp"), run: () => map.fit() } : undefined,
    });
  };

  return { writes, phase, pendingZone, refuse, guard, place, placeAtCenter };
}
